import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { Plus, Pencil, Trash2, RefreshCw, Save, X, ToggleLeft, ToggleRight, Gift, DollarSign, Trophy, Crown, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const ICON_OPTIONS = [
  { name: 'Gift',       Comp: Gift },
  { name: 'DollarSign', Comp: DollarSign },
  { name: 'Trophy',     Comp: Trophy },
  { name: 'Crown',      Comp: Crown },
  { name: 'Users',      Comp: Users },
];

const COLOR_OPTIONS = ['#10B981', '#22C55E', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#FCD34D', '#EF4444'];

const EMPTY = {
  count: 1,
  reward: '',
  sub: '',
  accent: '#8B5CF6',
  icon: 'Trophy',
  active: true,
  sort_order: 0,
};

export default function ReferralOffersPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | offer object | 'new'
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/referral-offers');
      setOffers(res.data?.offers || []);
    } catch (e) {
      toast.error('Failed to load referral offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    try {
      const res = await api.post('/api/admin/referral-offers/seed-defaults');
      toast.success(`Seeded ${res.data?.created || 0} default offers`);
      load();
    } catch (e) {
      toast.error('Seed failed');
    }
  };

  const startNew = () => {
    setEditing('new');
    setForm({ ...EMPTY, sort_order: (offers.length + 1) });
  };

  const startEdit = (o) => {
    setEditing(o);
    setForm({
      count: o.count,
      reward: o.reward,
      sub: o.sub,
      accent: o.accent,
      icon: o.icon,
      active: o.active,
      sort_order: o.sort_order,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.reward.trim()) { toast.error('Reward text is required'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/api/admin/referral-offers', form);
        toast.success('Offer created');
      } else {
        await api.put(`/api/admin/referral-offers/${editing.id}`, form);
        toast.success('Offer updated');
      }
      await load();
      cancelEdit();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (o) => {
    try {
      await api.put(`/api/admin/referral-offers/${o.id}`, { ...o, active: !o.active });
      load();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete offer "${o.reward}"?`)) return;
    try {
      await api.delete(`/api/admin/referral-offers/${o.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-referral-offers">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Referral Offers</h2>
          <p className="text-sm text-gray-400">Configure the rewards shown on the player Refer &amp; Earn page.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-white/10" data-testid="ro-reload">
            <RefreshCw className="w-4 h-4 mr-1" /> Reload
          </Button>
          {offers.length === 0 && (
            <Button variant="outline" onClick={seedDefaults} className="border-emerald-500/40 text-emerald-300" data-testid="ro-seed">
              Seed Defaults
            </Button>
          )}
          <Button onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ro-new">
            <Plus className="w-4 h-4 mr-1" /> New Offer
          </Button>
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="ro-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">{editing === 'new' ? 'New Offer' : 'Edit Offer'}</h3>
            <button onClick={cancelEdit} className="p-2 rounded-lg hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Friends required (count)">
              <Input
                type="number"
                min={0}
                value={form.count}
                onChange={(e) => setForm({ ...form, count: parseInt(e.target.value || '0', 10) })}
                className="bg-[#0B0E14] border-white/10"
                data-testid="ro-field-count"
              />
            </Field>
            <Field label="Sort order">
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || '0', 10) })}
                className="bg-[#0B0E14] border-white/10"
                data-testid="ro-field-order"
              />
            </Field>
            <Field label="Reward (headline)" full>
              <Input
                value={form.reward}
                onChange={(e) => setForm({ ...form, reward: e.target.value })}
                placeholder="e.g. +$25 Bonus"
                className="bg-[#0B0E14] border-white/10"
                data-testid="ro-field-reward"
              />
            </Field>
            <Field label="Subtitle / description" full>
              <Input
                value={form.sub}
                onChange={(e) => setForm({ ...form, sub: e.target.value })}
                placeholder="e.g. Refer 5 active friends"
                className="bg-[#0B0E14] border-white/10"
                data-testid="ro-field-sub"
              />
            </Field>
            <Field label="Icon">
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map(({ name, Comp }) => {
                  const sel = form.icon === name;
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm({ ...form, icon: name })}
                      className="px-3 py-2 rounded-lg flex items-center gap-1.5 transition"
                      style={{
                        background: sel ? `${form.accent}33` : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${sel ? form.accent : 'rgba(255,255,255,0.08)'}`,
                        color: sel ? form.accent : '#fff',
                      }}
                      data-testid={`ro-icon-${name}`}
                    >
                      <Comp className="w-4 h-4" />
                      <span className="text-xs">{name}</span>
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Accent color">
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => {
                  const sel = form.accent === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, accent: c })}
                      className="w-9 h-9 rounded-lg transition"
                      style={{ background: c, border: sel ? '3px solid #fff' : '1px solid rgba(255,255,255,0.15)' }}
                      data-testid={`ro-color-${c.replace('#','')}`}
                      aria-label={c}
                    />
                  );
                })}
              </div>
            </Field>
            <Field label="Status" full>
              <button
                type="button"
                onClick={() => setForm({ ...form, active: !form.active })}
                className="px-4 py-2 rounded-lg flex items-center gap-2 transition w-fit"
                style={{
                  background: form.active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  border: `1px solid ${form.active ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`,
                  color: form.active ? '#22FF88' : '#FCA5A5',
                }}
                data-testid="ro-toggle-active"
              >
                {form.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {form.active ? 'Active' : 'Inactive'}
              </button>
            </Field>
          </div>
          <div className="flex gap-2 justify-end mt-5">
            <Button variant="outline" onClick={cancelEdit} className="border-white/10">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ro-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Offer'}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#131822]">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0E14] text-gray-400 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="px-3 py-3 text-left">Order</th>
              <th className="px-3 py-3 text-left">Icon</th>
              <th className="px-3 py-3 text-left">Friends</th>
              <th className="px-3 py-3 text-left">Reward</th>
              <th className="px-3 py-3 text-left">Subtitle</th>
              <th className="px-3 py-3 text-left">Color</th>
              <th className="px-3 py-3 text-center">Active</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && offers.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                No offers yet. Click <span className="text-emerald-400">Seed Defaults</span> or <span className="text-emerald-400">New Offer</span>.
              </td></tr>
            )}
            {offers.map((o) => {
              const Icon = (ICON_OPTIONS.find(i => i.name === o.icon) || ICON_OPTIONS[2]).Comp;
              return (
                <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`ro-row-${o.id}`}>
                  <td className="px-3 py-3">{o.sort_order}</td>
                  <td className="px-3 py-3">
                    <span
                      className="inline-flex w-8 h-8 rounded-lg items-center justify-center"
                      style={{ background: `${o.accent}22`, border: `1px solid ${o.accent}55` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: o.accent }} />
                    </span>
                  </td>
                  <td className="px-3 py-3 font-mono">{o.count}</td>
                  <td className="px-3 py-3 font-bold">{o.reward}</td>
                  <td className="px-3 py-3 text-gray-400 max-w-[260px] truncate">{o.sub}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-2 text-xs">
                      <span className="w-4 h-4 rounded" style={{ background: o.accent }} />
                      <span className="font-mono text-gray-400">{o.accent}</span>
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => toggleActive(o)} className="inline-flex items-center" data-testid={`ro-toggle-${o.id}`}>
                      {o.active
                        ? <ToggleRight className="w-6 h-6 text-emerald-400" />
                        : <ToggleLeft className="w-6 h-6 text-gray-500" />}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => startEdit(o)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`ro-edit-${o.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(o)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" data-testid={`ro-delete-${o.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-xs uppercase tracking-wider text-gray-400 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
